import {
  MobileDataTable,
  TRANSFER,
  removeEmptyFilters,
  useAccordionFilterBuilder,
  useDateRangeFilter,
  usePersistedBlotterTable,
  useWsBlotterTable,
  type BlotterColumnState,
  type Transfer,
} from '@talos/kyoko';
import { useMemo } from 'react';
import { useClientSideTransfersFilter } from '../../../../Blotters/Transfers/useClientSideTransfersFilter';
import { useTransfersColumns } from '../../../../Blotters/Transfers/useTransfersColumns';
import { useTransfersFilter, type TransfersBlotterFilter } from '../../../../Blotters/Transfers/useTransfersFilter';
import { MobileBlotterToolbar } from '../MobileBlotterToolbar';
import { MobileShowAllDecimalsToggle } from '../MobileShowAllDecimalsToggle';
import { MobileTransferCard } from './MobileTransferCard';

interface MobileFilteredTransfersProps {
  blotterID: string;
  defaultFilter?: TransfersBlotterFilter;
  defaultColumns?: BlotterColumnState[];
}

/**
 * Mobile counterpart to desktop `FilteredTransfers`. Reuses the same data observable,
 * column definitions, and filter logic — only the rendering layer changes
 * (`MobileDataTable` + `MobileTransferCard` instead of `BlotterTable`).
 *
 * Row taps open a {@link MobileRowDetailSheet} via the default
 * {@link MobileDataTable} behaviour — no explicit `onRowPress` needed.
 */
export function MobileFilteredTransfers({ blotterID, defaultFilter, defaultColumns }: MobileFilteredTransfersProps) {
  const columns = useTransfersColumns(defaultColumns);

  const persistedBlotterTable = usePersistedBlotterTable(blotterID, { filter: defaultFilter });

  const filterResults = useTransfersFilter({ persistedBlotterTable, defaultFilter });

  const clientLocalFilter = useClientSideTransfersFilter(filterResults.clientSideFilter);

  const filterBuilderAccordion = useAccordionFilterBuilder({
    filterBuilderProps: filterResults.filterBuilderProps,
  });

  const dateRangeFilter = useDateRangeFilter(filterResults.filter, filterResults.changeFilter);

  const request = useMemo(
    () => ({
      name: TRANSFER,
      tag: 'MobileTransfersBlotter',
      ...removeEmptyFilters(onlyServerFilterKeys(filterResults.filter)),
    }),
    [filterResults.filter]
  );

  const blotterTable = useWsBlotterTable({
    request,
    persistence: persistedBlotterTable,
    columns,
    rowID: 'rowID' satisfies keyof Transfer,
    clientLocalFilter,
    exportOptions: { name: 'Transfers' },
  });

  return (
    <>
      <MobileBlotterToolbar
        {...filterBuilderAccordion}
        {...dateRangeFilter}
        {...blotterTable.blotterTableFiltersProps}
        settingsContent={<MobileShowAllDecimalsToggle />}
      />
      <MobileDataTable<Transfer>
        {...blotterTable}
        cardRenderer={MobileTransferCard}
        cardHeight={160}
        detailsTitle="Transfer Details"
      />
    </>
  );
}

function onlyServerFilterKeys(filter: TransfersBlotterFilter | undefined) {
  if (!filter) {
    return filter;
  }
  return {
    StartDate: filter.StartDate,
    EndDate: filter.EndDate,
  } satisfies TransfersBlotterFilter;
}
